<template>
  <div class="max-w-4xl mx-auto pb-24 lg:pb-8 px-4 sm:px-0">
    <!-- Header de la sección -->
    <div class="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-sm p-5 border border-indigo-200 mb-6">
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="h-8 w-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        </div>
        <div class="ml-4 flex-1">
          <h3 class="text-lg font-bold text-indigo-900 mb-1">{{ t('userPanel.payments.title') }}</h3>
          <p class="text-sm text-indigo-700">{{ t('userPanel.payments.description') }}</p>
        </div>
      </div>
    </div>

    <!-- Estadísticas de pagos -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <!-- Total pagado -->
      <div class="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs text-gray-500 mb-1">Total Pagado</p>
            <p class="text-2xl font-bold text-gray-900">€{{ totalAmountPaid.toFixed(2) }}</p>
          </div>
          <div class="p-3 bg-green-100 rounded-lg">
            <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Pagos realizados -->
      <div class="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs text-gray-500 mb-1">Pagos Realizados</p>
            <p class="text-2xl font-bold text-gray-900">{{ totalPaymentsMade }}</p>
          </div>
          <div class="p-3 bg-blue-100 rounded-lg">
            <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Aplicaciones activas -->
      <div class="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs text-gray-500 mb-1">Aplicaciones</p>
            <p class="text-2xl font-bold text-gray-900">{{ userApplications.length }}</p>
          </div>
          <div class="p-3 bg-purple-100 rounded-lg">
            <svg class="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Estado de carga -->
    <div v-if="isLoadingPayments" class="bg-white rounded-xl shadow-sm p-8 border border-gray-200 text-center">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      <p class="mt-4 text-sm text-gray-600">Cargando pagos...</p>
    </div>

    <!-- Sin pagos registrados -->
    <div v-else-if="userApplications.length === 0" class="bg-white rounded-xl shadow-sm p-8 border border-gray-200 text-center">
      <svg class="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
      <h4 class="text-lg font-semibold text-gray-900 mb-2">No tienes pagos registrados</h4>
      <p class="text-sm text-gray-600 mb-4">Aplica a un coliving y completa los pagos para verlos aquí</p>
      <router-link 
        to="/findyourcoliving"
        class="inline-flex items-center px-4 py-2 bg-white text-indigo-600 border border-indigo-200 rounded-full hover:bg-indigo-50 hover:shadow-md shadow-sm transition-all"
      >
        Buscar Colivings
      </router-link>
    </div>

    <!-- Listado de aplicaciones con pagos -->
    <div v-else class="space-y-6">
      <div
        v-for="application in userApplications"
        :key="application.id"
        class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <!-- Header de la aplicación -->
        <div class="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 border-b border-indigo-100">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <h4 class="text-lg font-bold text-gray-900 mb-1">{{ application.colivingTitle || 'Coliving' }}</h4>
              <div class="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <span class="flex items-center">
                  <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Habitación #{{ application.assignedRoomData?.number || application.assignedRoom || 'N/A' }}
                </span>
                <span class="flex items-center">
                  <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {{ formatPaymentDate(application.createdAt) }}
                </span>
              </div>
            </div>
            <span 
              :class="[
                'px-3 py-1 rounded-full text-xs font-semibold',
                getApplicationStatusClass(application.status)
              ]"
            >
              {{ getApplicationStatusText(application.status) }}
            </span>
          </div>
        </div>

        <!-- Pagos de la aplicación -->
        <div class="p-4">
          <!-- Sin pagos en esta aplicación -->
          <div v-if="!application.paymentConfirmations || application.paymentConfirmations.length === 0" class="text-center py-6 text-gray-500">
            <svg class="h-12 w-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="text-sm">No hay pagos registrados para esta aplicación</p>
          </div>

          <!-- Lista de pagos -->
          <div v-else class="space-y-3">
            <div
              v-for="payment in application.paymentConfirmations"
              :key="payment.paymentId"
              class="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
            >
              <div class="flex items-start flex-1">
                <!-- Icono del pago -->
                <div class="flex-shrink-0 mr-3">
                  <div :class="[
                    'p-2 rounded-lg',
                    payment.paidViaStripe ? 'bg-green-100' : 'bg-blue-100'
                  ]">
                    <!-- Icono Stripe -->
                    <svg v-if="payment.paidViaStripe" class="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    <!-- Icono Manual -->
                    <svg v-else class="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>

                <!-- Información del pago -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center flex-wrap gap-2 mb-1">
                    <h5 class="text-sm font-semibold text-gray-900">
                      {{ getPaymentTypeName(payment.paymentId) }}
                    </h5>
                    <!-- Badge Stripe -->
                    <span v-if="payment.paidViaStripe" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      <svg class="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                      Stripe
                    </span>
                    <!-- Badge Manual -->
                    <span v-else class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      Manual
                    </span>
                  </div>
                  <p class="text-xs text-gray-500 mb-2">
                    Confirmado el {{ formatPaymentDateTime(payment.confirmedAt) }}
                  </p>
                  <p class="text-sm font-bold text-gray-900">€{{ payment.amount?.toFixed(2) || '0.00' }}</p>
                  
                  <!-- ID de transacción de Stripe -->
                  <div v-if="payment.stripeSessionId" class="mt-2 text-xs text-gray-500 font-mono bg-white p-2 rounded border border-gray-200">
                    ID: {{ payment.stripeSessionId }}
                  </div>
                </div>
              </div>

              <!-- Botón de ver coliving -->
              <div class="ml-4">
                <router-link
                  :to="`/colivings/${application.colivingId}`"
                  class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors"
                >
                  Ver detalles
                  <svg class="h-3 w-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer con acción -->
        <div class="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <router-link
            :to="`/colivings/${application.colivingId}`"
            class="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
          >
            <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Ver detalles del coliving
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore'
import { db } from '../firebase'

const { t } = useI18n()

// Props
const props = defineProps({
  currentUser: {
    type: Object,
    required: true
  }
})

// State
const userApplications = ref([])
const isLoadingPayments = ref(false)

// ========================================
// COMPUTED PROPERTIES
// ========================================

/**
 * Computed: Total de pagos realizados
 */
const totalPaymentsMade = computed(() => {
  return userApplications.value.reduce((total, app) => {
    return total + (app.paymentConfirmations?.length || 0)
  }, 0)
})

/**
 * Computed: Monto total pagado
 */
const totalAmountPaid = computed(() => {
  return userApplications.value.reduce((total, app) => {
    const appTotal = (app.paymentConfirmations || []).reduce((sum, payment) => {
      return sum + (payment.amount || 0)
    }, 0)
    return total + appTotal
  }, 0)
})

// ========================================
// FUNCIONES DE CARGA DE DATOS
// ========================================

/**
 * Cargar todas las aplicaciones del usuario con sus pagos
 * Lee directamente de Firestore: colivingApplications
 * Incluye el campo paymentConfirmations[] que contiene:
 * - Pagos confirmados con Stripe (paidViaStripe: true)
 * - Pagos confirmados manualmente con checkbox (paidViaStripe: false)
 */
async function loadUserApplications() {
  if (!props.currentUser) return
  
  isLoadingPayments.value = true
  try {
    // Query a Firestore: obtener aplicaciones del usuario
    const applicationsQuery = query(
      collection(db, 'colivingApplications'),
      where('applicantId', '==', props.currentUser.uid),
      orderBy('createdAt', 'desc')
    )
    
    const applicationsSnapshot = await getDocs(applicationsQuery)
    const applications = []
    
    // Para cada aplicación, obtener datos adicionales
    for (const docSnapshot of applicationsSnapshot.docs) {
      const appData = docSnapshot.data()
      
      // Obtener título del coliving
      let colivingTitle = 'Coliving'
      if (appData.colivingId) {
        try {
          const colivingDoc = await getDoc(doc(db, 'colivings', appData.colivingId))
          if (colivingDoc.exists()) {
            colivingTitle = colivingDoc.data().title || 'Coliving'
          }
        } catch (error) {
          console.error('Error cargando título del coliving:', error)
        }
      }
      
      applications.push({
        id: docSnapshot.id,
        colivingTitle,
        ...appData
      })
    }
    
    userApplications.value = applications
    
  } catch (error) {
    console.error('❌ [UserPayments] Error cargando aplicaciones del usuario:', error)
  } finally {
    isLoadingPayments.value = false
  }
}

// ========================================
// FUNCIONES DE FORMATO
// ========================================

/**
 * Formatear fecha para pagos
 */
function formatPaymentDate(date) {
  if (!date) return 'N/A'
  
  let dateObj
  if (date.toDate) {
    dateObj = date.toDate()
  } else if (date instanceof Date) {
    dateObj = date
  } else {
    dateObj = new Date(date)
  }
  
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(dateObj)
}

/**
 * Formatear fecha y hora para pagos
 */
function formatPaymentDateTime(date) {
  if (!date) return 'N/A'
  
  let dateObj
  if (date.toDate) {
    dateObj = date.toDate()
  } else if (date instanceof Date) {
    dateObj = date
  } else {
    dateObj = new Date(date)
  }
  
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(dateObj)
}

/**
 * Obtener nombre del tipo de pago
 */
function getPaymentTypeName(paymentId) {
  if (paymentId === 'initial') return 'Pago Inicial (Fianza + Primer mes)'
  if (paymentId.startsWith('month-')) {
    const monthNumber = paymentId.replace('month-', '')
    return `Renta Mensual - Mes ${monthNumber}`
  }
  return paymentId
}

/**
 * Obtener clase CSS para el estado de la aplicación
 */
function getApplicationStatusClass(status) {
  const classes = {
    'submitted': 'bg-blue-100 text-blue-800',
    'pre_approved': 'bg-yellow-100 text-yellow-800',
    'contract_signed': 'bg-green-100 text-green-800',
    'payment_completed': 'bg-emerald-100 text-emerald-800',
    'room_assigned': 'bg-purple-100 text-purple-800',
    'rejected': 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

/**
 * Obtener texto del estado de la aplicación
 */
function getApplicationStatusText(status) {
  const texts = {
    'submitted': 'Enviada',
    'pre_approved': 'Pre-aprobada',
    'contract_signed': 'Contrato firmado',
    'payment_completed': 'Pago completado',
    'room_assigned': 'Inquilino activo',
    'rejected': 'Rechazada'
  }
  return texts[status] || status
}

// ========================================
// LIFECYCLE & WATCHERS
// ========================================

// Cargar aplicaciones al montar
onMounted(async () => {
  if (props.currentUser) {
    await loadUserApplications()
  }
})

// Recargar cuando cambie el usuario
watch(() => props.currentUser, async (newUser) => {
  if (newUser) {
    await loadUserApplications()
  } else {
    userApplications.value = []
  }
}, { deep: true })
</script>

<style scoped>
/* Animación suave para las tarjetas */
.transition-colors {
  transition: all 0.2s ease-in-out;
}

/* Scroll suave en móvil */
@media (max-width: 640px) {
  .overflow-x-auto {
    -webkit-overflow-scrolling: touch;
  }
}
</style>
